import {
  DatePicker,
  Field,
  Input,
  RadioGroup,
  Spin,
} from '@/components/commons';
import {
  FALLBACK_URL,
  FILE_SIZE_LIMIT,
  SUPPORTED_EXTENSION,
  SUPPORTED_FORMATS,
} from '@/services/image';
import { postCodeTypeOptions, Status, statusOptions } from '@/utils';
import { CloseOutlined } from '@ant-design/icons';
import { Badge, Button, Form, Image, Upload } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import moment, { Moment } from 'moment';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { onlyHalfSizeRegex } from './formSchemaFn';
import { IGetDetailEventResponse } from '@/types/response';

interface IProps {
  isLoading: boolean;
  openDrawer: boolean;
  checkZipCode: any;
  isLoadingCheckZipCode: boolean;
  event?: IGetDetailEventResponse;
}

export const EventForm: React.FC<IProps> = ({
  isLoading,
  openDrawer,
  checkZipCode,
  isLoadingCheckZipCode,
  event,
}) => {
  const { trigger, watch, setValue, setError, clearErrors } = useFormContext();

  const status = watch('status');

  const disabledDate = (current: Dayjs) => {
    return current.isBefore(dayjs().startOf('day'));
  };

  const disabledTime = (current: Dayjs) => {
    if (!current) return {};

    const now = dayjs();
    const selectedDate = dayjs(current);

    if (selectedDate.isBefore(now, 'day')) {
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
        disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
      };
    }

    if (selectedDate.isSame(now, 'day')) {
      const disabledHours: number[] = [];
      for (let i = 0; i < now.hour(); i++) {
        disabledHours.push(i);
      }

      const disabledMinutes = (selectedHour: number) => {
        if (selectedHour === now.hour()) {
          const minutes = [];
          for (let i = 0; i <= now.minute(); i++) {
            minutes.push(i);
          }
          return minutes;
        }
        return [];
      };

      return {
        disabledHours: () => disabledHours,
        disabledMinutes: disabledMinutes,
      };
    }

    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
    };
  };

  const isDisabled = watch('isDisabled');
  const feeType = watch('feeType');
  const imageBanner = watch('imageBanner');
  const attachments = watch('attachments');
  const postCodeType = watch('postCodeType');
  const zipCode = watch('zipCode');
  const keepCheckZipCodeError = watch('keepCheckZipCodeError');

  const handleUpload = (info: any) => {
    const file = info.file.originFileObj || info.file;
    const reader = new FileReader();
    reader.onload = () => {
      setValue('imageBanner', reader.result as string);
      setValue('attachments', info);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setValue('imageBanner', null);
    setValue('attachments', null);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const char = String.fromCharCode(event.which);
    if (!/[0-9]/.test(char)) {
      event.preventDefault();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const clipboardData = event.clipboardData.getData('text');
    if (!/^[0-9]*$/.test(clipboardData)) {
      event.preventDefault();
    }
  };

  const handleKeyPressInputPostCode = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    const char = String.fromCharCode(event.which);
    const inputValue = (event.target as HTMLInputElement).value;

    if (!/[0-9]/.test(char)) {
      event.preventDefault();
    }
  };

  const handlePasteInputPostCode = (
    event: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    const clipboardData = event.clipboardData.getData('text');
    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement.value;

    const selectionStart = inputElement.selectionStart ?? 0;
    const selectionEnd = inputElement.selectionEnd ?? 0;

    const newLength =
      inputValue.length -
      (selectionEnd - selectionStart) +
      clipboardData.length;

    if (!/^[0-9]*$/.test(clipboardData) || newLength > 7) {
      event.preventDefault();
    }
  };

  const beforeUpload = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (file.size > FILE_SIZE_LIMIT) {
      setError('attachment', {
        type: 'validate',
        message: '画像のサイズは10MBを超えています。',
      });
      return Upload.LIST_IGNORE;
    }

    if (
      !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
      !SUPPORTED_FORMATS.includes(file.type)
    ) {
      setError('attachment', {
        type: 'validate',
        message: '画像は .jpg、.png、.jpeg 形式ではありません。',
      });
      return Upload.LIST_IGNORE;
    }
    clearErrors('attachment');

    return true;
  };

  useEffect(() => {
    if (keepCheckZipCodeError) {
      setValue('keepCheckZipCodeError', null);
    }
  }, [zipCode]);

  const onBlurInput = (e: any) => {
    if (!/^[0-9]*$/.test(e.target.value)) {
      setValue('zipCode', '');
      setError('zipCode', {
        type: 'validate',
        message: '郵便番号は未入力です。',
      });
    }
  };

  const handleCheckZipCode = (zipCode: string) => {
    if (zipCode) {
      if (!onlyHalfSizeRegex.test(zipCode)) {
        checkZipCode(zipCode);
      }
    } else {
      setError('zipCode', {
        type: 'validate',
        message: '郵便番号は未入力です。',
      });
    }
  };

  const isPublic = useMemo(
    () =>
      !!event &&
      event.status === Status.PUBLIC &&
      moment(event.publicAt).isSameOrBefore(moment()),
    [event],
  );

  useEffect(() => {
    if (isPublic && status === Status.PUBLIC && event?.publicAt) {
      setValue('publicAt', moment(event.publicAt));
    }
  }, [status, isPublic, event, setValue]);

  return (
    <Form layout='vertical'>
      <Spin spinning={isLoading}>
        <Field label='タイトル' name='name' required>
          <Input placeholder='タイトル' maxLength={40} disabled={isDisabled} />
        </Field>
        <div className='flex items-baseline gap-2'>
          <div className='flex-1'>
            <span className='title-calendar font-semibold'>
              開催開始日時
              <Badge status='error' className='ml-2' />
            </span>
          </div>
          <div className='flex-1'>
            <span className='title-calendar font-semibold ml-2'>
              開催終了日時
            </span>
            <Badge status='error' className='ml-2' />
          </div>
        </div>

        <div className='flex items-baseline	gap-1 pt-2'>
          <Field name='eventFrom' className='flex-1'>
            <DatePicker
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              showTime
              onCalendarChange={(e) => {
                setValue('eventFrom', e ? (e as Moment).second(0) : undefined);
                trigger('eventFrom');
                trigger('eventTo');
              }}
              disabled={isDisabled}
            />
          </Field>
          <span>～</span>
          <Field name='eventTo' className='flex-1'>
            <DatePicker
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              showTime
              onCalendarChange={(e) => {
                setValue('eventTo', e ? (e as Moment).second(0) : undefined);
                trigger('eventFrom');
                trigger('eventTo');
              }}
              disabled={isDisabled}
            />
          </Field>
        </div>
        <Field label='本文' name='content' required>
          {/* <TextArea
            placeholder='イベント概要'
            rows={3}
            maxLength={500}
            disabled={isDisabled}
          /> */}
          <Input placeholder='本文' maxLength={500} disabled={isDisabled} />
        </Field>
        <Field label='CTA文言' name='ctaText' required>
          <Input placeholder='CTA文言' maxLength={40} disabled={isDisabled} />
        </Field>
        <Field label=' 遷移先' name='url' required>
          <Input placeholder=' 遷移先' maxLength={2000} disabled={isDisabled} />
        </Field>
        {!attachments?.fileList?.length && !imageBanner && (
          <Field
            label='カバー画像'
            name='attachments'
            className='h-[80px]'
            required
          >
            <div className='border-solid rounded border-2 border-zinc-300  w-[200px] h-[100px] flex justify-center items-center'>
              <Upload
                customRequest={() => {}}
                disabled={isDisabled}
                listType='text'
                maxCount={1}
                beforeUpload={beforeUpload}
                showUploadList={false}
                onChange={handleUpload}
              >
                <Button
                  disabled={isDisabled}
                  type='default'
                  shape='default'
                  size='middle'
                  className='text-[#84ab42] border border-[#84ab42] font-semibold'
                >
                  画像を選択
                </Button>
              </Upload>
            </div>
          </Field>
        )}
        {imageBanner && openDrawer && !isLoading && (
          <Field
            label='カバー画像'
            name='imageBanner'
            className='h-[80px]'
            required
          >
            <div className='relative inline-block'>
              <Image
                height={100}
                src={imageBanner}
                className='block rounded-lg'
                fallback={FALLBACK_URL}
              />
              {!isDisabled && (
                <Button
                  type='primary'
                  shape='circle'
                  icon={<CloseOutlined />}
                  size='small'
                  onClick={handleRemoveImage}
                  className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
                />
              )}
            </div>
          </Field>
        )}
        <label className='title-calendar font-medium text-xs mt-[34px] absolute'>
          <span className='font-bold mr-1'>※</span>
          1.8:1(~375×211 px)の写真（正方形）をアップロードしてください
        </label>
        <Field name='attachment' className='mt-11'>
          <div></div>
        </Field>

        <div className='flex items-baseline gap-2'>
          <div className='flex-1'>
            <span className='title-calendar font-semibold'>
              公開日時
              <Badge status='error' className='ml-2' />
            </span>
          </div>
          <div className='flex-1'>
            <span className='title-calendar font-semibold ml-2'>
              公開終了日時
            </span>
          </div>
        </div>
        <div className='flex items-baseline	gap-1 pt-2'>
          <Field name='publicAt' className='flex-1'>
            <DatePicker
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              showTime
              onCalendarChange={(e) => {
                setValue('publicAt', e ? (e as Moment).second(0) : undefined);
                trigger('eventFrom');
                trigger('publicAt');
                trigger('publicEndAt');
              }}
              disabled={isDisabled || (isPublic && status !== Status.DRAFT)}
            />
          </Field>
          <span>～</span>
          <Field name='publicEndAt' className='flex-1'>
            <DatePicker
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              showTime
              onCalendarChange={(e) => {
                setValue(
                  'publicEndAt',
                  e ? (e as Moment).second(0) : undefined,
                );
                trigger('publicEndAt');
                trigger('eventTo');
              }}
              disabled={isDisabled}
            />
          </Field>
        </div>
        <label className='title-calendar font-semibold '>配信範囲</label>
        <Field
          name='postCodeType'
          className={`w-full ${!!postCodeType && 'mb-1'} `}
        >
          <RadioGroup options={postCodeTypeOptions} disabled={isDisabled} />
        </Field>
        {!!postCodeType && (
          <>
            <div className='flex items-baseline'>
              <Field name='zipCode' className='mb-0'>
                <Input
                  className='w-[200px] mr-1 bg-transparent hover:bg-transparent'
                  disabled={isDisabled}
                  onKeyPress={handleKeyPressInputPostCode}
                  onPaste={handlePasteInputPostCode}
                  onBlur={onBlurInput}
                  maxLength={7}
                />
              </Field>
              <Button
                className='absolute left-[210px] pt-1'
                disabled={isDisabled}
                type='primary'
                size='middle'
                onClick={() => handleCheckZipCode(zipCode)}
                loading={isLoadingCheckZipCode}
              >
                住所検索
              </Button>
            </div>
            <Field name='note' className='mb-1'>
              <div>
                <span>※ハイフンなし半角で入力してください</span>
                <span className='mt-[2px] block'>
                  配信する都道府県・市区町村
                </span>
              </div>
            </Field>
            <Field name='address1' className='mb-1'>
              <Input
                placeholder='-'
                maxLength={200}
                disabled={true}
                style={!isDisabled ? { color: 'black' } : {}}
              />
            </Field>
            <Field name='address2'>
              <Input
                placeholder='-'
                maxLength={200}
                disabled={true}
                style={!isDisabled ? { color: 'black' } : {}}
              />
            </Field>
          </>
        )}

        <Field label='公開ステータス' name='status' className='w-full'>
          <RadioGroup options={statusOptions} disabled={isDisabled} />
        </Field>
      </Spin>
    </Form>
  );
};
